import * as tslib_1 from "tslib";
import getContactByPersonaId from './../../../services/people/getContactByPersonaId';
import getPersonaByEmail from './../../../util/getPersonaByEmail';
import synchronizeSearchFolder from './synchronizeSearchFolder';
import datapoints from '../../../datapoints';
import * as pendingFavoritesMapMutators from '../../../mutators/pendingFavoritesMapMutators';
import { logUsage } from 'owa-analytics';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { getGuid } from 'owa-guid';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import { markSearchFolderAsPopulated, MARK_FOLDER_POPULATED_TIMEOUT, } from './markSearchFolderAsPopulated';
import { getStore as getSharedFavoritesStore, isPersonaInFavorites, updateFavoritesUserOption, } from 'owa-favorites';
import isFavoritingInProgress from '../../../selectors/isFavoritingInProgress';
/**
 * Add a favorite persona given the personaId or email.
 * If personaId is provided, it will be used to resolve the persona, and the email will be ignored
 * @returns A promise wrapping the added FavoritePersonaNode
 */
export default function addFavoritePersona(personaId, emailAddress, displayName, actionSource) {
    if (personaId) {
        return addFavoritePersonaByPersonaId(personaId, displayName, actionSource);
    }
    else {
        return addFavoritePersonaByEmail(emailAddress, displayName, actionSource);
    }
}
/**
 * Add a favorite node to the favorite store, the persona is resolved by calling findPeople with the email address
 */
function addFavoritePersonaByEmail(emailAddress, displayName, actionSource) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        return tslib_1.__generator(this, function (_a) {
            if (!emailAddress) {
                throw new Error('No email address was provided.');
            }
            return [2 /*return*/, resolveAndAddFavoritePersona(null, emailAddress, displayName, actionSource, 'email', function () { return getPersonaByEmail(emailAddress, displayName, false /*inludeMailboxAndDirectory*/); })];
        });
    });
}
/**
 * Add a favorite node to the favorite store, the persona is resolved by calling GetPersona with the persona id
 */
function addFavoritePersonaByPersonaId(personaId, displayName, actionSource) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        return tslib_1.__generator(this, function (_a) {
            if (!personaId) {
                throw new Error('No persona id was provided.');
            }
            return [2 /*return*/, resolveAndAddFavoritePersona(personaId, null, displayName, actionSource, 'personaId', function () { return getContactByPersonaId(personaId); })];
        });
    });
}
var resolveAndAddFavoritePersona = action('AddFavoritePersona', datapoints.AddFavoritePersonaAction)(function resolveAndAddFavoritePersona(personaId, emailAddress, displayName, actionSource, valueForResolution, contactResolver) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var sharedFavoritesStore, inProgressKey, isInProgressSet, favoriteNodeId, initialFavoritePersonaNode, persona_1, updatedFavoritePersonaNode, error_1, _a, eventName, customData;
        return tslib_1.__generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    sharedFavoritesStore = getSharedFavoritesStore();
                    inProgressKey = emailAddress || personaId;
                    isInProgressSet = false;
                    favoriteNodeId = getGuid();
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 6, , 7]);
                    if (isFavoritingInProgress(inProgressKey)) {
                        throw new Error('AddFavoritePersona - Favoriting is already in progress');
                    }
                    pendingFavoritesMapMutators.add(inProgressKey);
                    isInProgressSet = true;
                    // Check if favoritesPersonaNodes already contains this persona, by checking personaId and emails
                    if (isPersonaInFavorites(personaId, emailAddress)) {
                        throw new Error('AddFavoritePersona - Cannot add duplicate personas to favorites. Store already has persona before Persona resolution');
                    }
                    initialFavoritePersonaNode = addInitialFavoritePersonaNode(favoriteNodeId, displayName, emailAddress);
                    return [4 /*yield*/, resolvePersona(displayName, emailAddress, contactResolver)];
                case 2:
                    persona_1 = _b.sent();
                    // Check if favoritesPersonaNodes already contains this persona, by checking personaId
                    if (persona_1.personaId &&
                        tslib_1.__spread(sharedFavoritesStore.favoritesPersonaNodes.values()).some(function (node) { return node.personaId === persona_1.personaId; })) {
                        throw new Error('AddFavoritePersona - Cannot add duplicate personas to favorites. Store already has persona after Persona resolution.');
                    }
                    updatedFavoritePersonaNode = updateFavoritePersonaNode(favoriteNodeId, persona_1, initialFavoritePersonaNode, sharedFavoritesStore);
                    if (!updatedFavoritePersonaNode.mainEmailAddress) return [3 /*break*/, 4];
                    return [4 /*yield*/, synchronizeSearchFolder(updatedFavoritePersonaNode, 'OnAdd').catch(function (error) {
                            throw new Error('AddFavoritePersona - synchronizeSearchFolder failed with error: ' +
                                (error || {}).toString());
                        })];
                case 3:
                    _b.sent();
                    _b.label = 4;
                case 4: return [4 /*yield*/, updateFavoritesUserOption()];
                case 5:
                    _b.sent();
                    setTimeout(function () {
                        markSearchFolderAsPopulated(favoriteNodeId);
                    }, MARK_FOLDER_POPULATED_TIMEOUT);
                    pendingFavoritesMapMutators.remove(inProgressKey);
                    return [2 /*return*/, updatedFavoritePersonaNode];
                case 6:
                    error_1 = _b.sent();
                    // Remove the node from the leftNav
                    cleanup(favoriteNodeId, sharedFavoritesStore);
                    if (isInProgressSet) {
                        pendingFavoritesMapMutators.remove(inProgressKey);
                    }
                    _a = datapoints.AddFavoritePersonaFailure.datapoint, eventName = _a.name, customData = _a.customData;
                    trace.warn(eventName + ': ' + error_1);
                    logUsage(eventName, customData(error_1.toString()));
                    throw tslib_1.__assign({ type: 'AddFavoritePersonaError' }, error_1);
                case 7: return [2 /*return*/];
            }
        });
    });
});
/**
 * Resolve the favorite with the provided resolver, if the favorite cannot be resolved a personaType with the provided
 * information is with an Undefined Id.
 */
function resolvePersona(displayName, emailAddress, contactResolver) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, contactResolver()
                        .then(function (personaType) {
                        // If we cannot resolve the address we have a recipient cache persona without a valid persona id,
                        // so we do not define the persona id.
                        return personaType
                            ? {
                                displayName: personaType.DisplayName || displayName,
                                personaId: personaType.PersonaId.Id,
                                emailAddress: personaType.EmailAddress &&
                                    getEmailAddressNameOrAddress(personaType.EmailAddress),
                                emailAddresses: (personaType.EmailAddresses &&
                                    personaType.EmailAddresses.map(getEmailAddressNameOrAddress)) ||
                                    [],
                            }
                            : {
                                displayName: displayName,
                                personaId: undefined,
                                emailAddress: emailAddress,
                                emailAddresses: [emailAddress],
                            };
                    })
                        .catch(function (error) {
                        throw new Error('AddFavoritePersona - resolvePersona failed with error: ' + (error || {}).toString());
                    })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
function addInitialFavoritePersonaNode(favoriteNodeId, displayName, emailAddress) {
    // At this point, the "type" of favorite is not known, so no personaId is present
    var favoritePersona = {
        id: favoriteNodeId,
        type: 1 /* Persona */,
        displayName: displayName,
        mainEmailAddress: emailAddress,
        allEmailAddresses: emailAddress ? [emailAddress] : [],
        searchFolderId: undefined,
        isSearchFolderPopulated: false,
        isJustAdded: true,
        isSyncUpdateDone: true,
        dropViewState: createDropViewState(),
    };
    var sharedFavoritesStore = getSharedFavoritesStore();
    sharedFavoritesStore.favoritesPersonaNodes.set(favoriteNodeId, favoritePersona);
    sharedFavoritesStore.orderedFavoritesNodeIds.push(favoriteNodeId);
    return favoritePersona;
}
function updateFavoritePersonaNode(favoriteNodeId, persona, currentNode, sharedFavoritesStore) {
    if ((currentNode.displayName || '') !== (persona.displayName || '')) {
        logUsage('AddFavoritePersona - DisplayName Mismatch');
    }
    var favoritePersona = tslib_1.__assign({}, currentNode, { displayName: persona.displayName, mainEmailAddress: persona.emailAddress, allEmailAddresses: persona.emailAddresses, isJustAdded: false });
    // The personaId field will be populated only if a contact from the myContacts folder was found
    if (persona.personaId) {
        logUsage('AddFavoritePersona - Contact found, storing personaId');
        favoritePersona.personaId = persona.personaId;
    }
    getSharedFavoritesStore().favoritesPersonaNodes.set(favoriteNodeId, favoritePersona);
    return favoritePersona;
}
function cleanup(personaId, sharedFavoritesStore) {
    var index = sharedFavoritesStore.orderedFavoritesNodeIds.indexOf(personaId);
    if (index > -1) {
        sharedFavoritesStore.orderedFavoritesNodeIds.splice(index, 1);
    }
    if (sharedFavoritesStore.favoritesPersonaNodes.has(personaId)) {
        sharedFavoritesStore.favoritesPersonaNodes.delete(personaId);
    }
}
/**
 * - The OWS FindPeople API returns an X500 address for some contacts. Use the OriginalDisplayName in those cases.
 * - If a persona was created with an email address in an invalid format (E.g.: foobar@example), then the EmailAddressWrapper
 *   from the GetPersona response will have an empty EmailAdress field. In that case, we return the OriginalDisplayName,
 *   which will be populated with the invalid format address.
 */
function getEmailAddressNameOrAddress(emailAddress) {
    return emailAddress.RoutingType && emailAddress.RoutingType.toUpperCase() === 'EX'
        ? emailAddress.OriginalDisplayName
        : emailAddress.EmailAddress || emailAddress.OriginalDisplayName;
}
//# sourceMappingURL=addFavoritePersonaV1.js.map